import { useQuery } from '@tanstack/react-query'

import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { usePriceContext } from '@/hooks/usePriceContext'
import { useTranslations } from '@/hooks/useTranslations'
import { getPricesKeys, normalizeQuantity } from './queryKeys'

import type { LoadedPrice } from './priceBatchLoader'

export interface UseProductPriceOptions {
  /** Gate fetching (e.g. wire to viewport visibility). Defaults to true. */
  enabled?: boolean
}

export interface UseProductPriceResult {
  priceData: LoadedPrice
  isLoading: boolean
}

/**
 * Reactive price for a single product+quantity. Backed by a per-product React Query key fed by the
 * shared batching loader: rendering 500 of these triggers a handful of bulk requests, each product is
 * cached independently (adding products never refetches existing ones), and a quantity change refetches
 * only that product. Pass `enabled` to lazy-load by viewport.
 */
export const useProductPrice = (
  productId: string | undefined,
  quantity = 1,
  options?: UseProductPriceOptions
): UseProductPriceResult => {
  const { load, customerId, orgUnitId, baseSiteId, pricesEnabled } = usePriceContext()
  const { translate } = useTranslations()

  const debouncedQuantity = useDebouncedValue(quantity)
  const effectiveQuantity = normalizeQuantity(debouncedQuantity)
  const wantsPrice = pricesEnabled && productId != null
  const fetchEnabled = wantsPrice && (options?.enabled ?? true)

  const query = useQuery({
    queryKey: getPricesKeys.product({
      customerId,
      orgUnitId,
      baseSiteId,
      productId: productId ?? '',
      quantity: effectiveQuantity,
    }),
    queryFn: () => load({ productId: productId as string, quantity: effectiveQuantity }),
    enabled: fetchEnabled,
    staleTime: Number.POSITIVE_INFINITY,
    meta: { errorMessage: translate('common.api_error') },
  })

  const priceData = query.data ?? null
  // Show a loading placeholder while a price is expected but not yet resolved — this also covers the
  // window before the element scrolls into view (fetch not enabled yet). A query that resolves to a
  // null price (product has no price) is settled, so it stops loading instead of spinning forever.
  const isLoading = wantsPrice && priceData == null && !query.isSuccess && !query.isError

  return { priceData, isLoading }
}
